/**
 * Job Equipment Planning Component
 *
 * Color Scheme (uses centralized EQUIPMENT_STATUS colors):
 * - Available/Stock (Green #4caf50): Items currently in stock
 * - Received/Assigned (Green #4caf50): Items received and assigned to job
 * - Checked Out (Purple #9c27b0): Items checked out to technician
 * - Installed (Blue #2196f3): Items installed at job site
 * - Ordered (Purple #9c27b0): Items that have been ordered (in transit)
 * - Still Needed (Orange/warning): Items that still need to be ordered or found
 *
 * Colors match equipment status across entire app for consistency.
 */
import React, { useState, useEffect, useCallback } from 'react';
import EnhancedJobPlanning from './EnhancedJobPlanning';
import ReceivingDashboard from './ReceivingDashboard';
import RecentActivity from './RecentActivity';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Circle as CircleIcon,
  History as HistoryIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  Print as PrintIcon,
  StickyNote2 as NotesIcon,
  QrCodeScanner as ScanIcon,
  QrCode as QrCodeIcon,
  MoreVert as MoreVertIcon,
  UnfoldMore as UnfoldMoreIcon,
  Info as InfoIcon,
  Undo as UndoIcon,
  AssignmentReturn as ReturnIcon,
  ListAlt as ClipboardList,
  Home as HomeIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api, { planningApi, jobApi, equipmentApi } from '../services/api.service';
import * as equipmentService from '../services/equipment.service';
import { getActivityLogs } from '../services/activity.service';
import { getActivityDisplay } from '../constants/activityTypes';
import StatusHistoryDialog from './StatusHistoryDialog';
import LinkifiedText from './LinkifiedText';
import BarcodeScanner from './BarcodeScanner';
import { decodeHTMLEntities } from '../utils/htmlDecoder';
import PlanningActivityTimeline from './JobEquipmentPlanning/PlanningActivityTimeline';
import { EQUIPMENT_STATUS, getStatusDisplay } from '../constants/equipmentStatus';
import { printSingleQRCode } from '../services/qrCodePrintService';

// PlanningActivityTimeline component extracted to separate file

/**
 * JobEquipmentPlanning - Catherine's Planning Workflow
 * 
 * Allows planning equipment types and quantities based on what was sold to client.
 * Creates foundation for Ross's warehouse receiving and assignment workflow.
 */
export default function JobEquipmentPlanning({ jobId, siteId = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  // Core state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [equipmentLibrary, setEquipmentLibrary] = useState([]);

  // Add equipment dialog state
  const [addDialog, setAddDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [plannedQuantity, setPlannedQuantity] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);

  // Edit mode state
  const [editingId, setEditingId] = useState(null);
  const [editQuantity, setEditQuantity] = useState(0);
  const [editSite, setEditSite] = useState(null);

  // Individual item tracking state
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [individualItems, setIndividualItems] = useState({});

  // Status history dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedEquipmentForHistory, setSelectedEquipmentForHistory] = useState(null);

  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Status change menu state
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuSelectedItem, setStatusMenuSelectedItem] = useState(null);
  
  // Individual item details expansion state
  const [expandedItemDetails, setExpandedItemDetails] = useState(new Set());
  const [newNote, setNewNote] = useState('');
  
  // Item notes dialog state
  const [itemNotesDialog, setItemNotesDialog] = useState(false);
  const [selectedItemForNotes, setSelectedItemForNotes] = useState(null);
  const [selectedPlanForItemNotes, setSelectedPlanForItemNotes] = useState(null);
  const [newItemNote, setNewItemNote] = useState('');

  // Order dialog state
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedPlanForOrder, setSelectedPlanForOrder] = useState(null);
  const [orderFormData, setOrderFormData] = useState({
    orderedQuantity: 0,
    orderDate: new Date().toISOString().split('T')[0],
    vendor: '',
    orderReference: '',
    notes: ''
  });

  // Template creation state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({
    manufacturer: '',
    model: '',
    description: '',
    categoryId: '',
    barcodePattern: '',
    notes: ''
  });
  const [pendingTemplateSelection, setPendingTemplateSelection] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newlyCreatedTemplateIds, setNewlyCreatedTemplateIds] = useState(new Set());
  
  // Barcode scanner dialog state
  const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
  const [scannerCallback, setScannerCallback] = useState(null);

  // Installed dialog state
  const [installedDialogOpen, setInstalledDialogOpen] = useState(false);
  const [selectedPlanForInstalled, setSelectedPlanForInstalled] = useState(null);
  const [installedFormData, setInstalledFormData] = useState({
    installedQuantity: 0,
    notes: ''
  });

  // Activity timeline refresh trigger
  const [activityRefreshTrigger, setActivityRefreshTrigger] = useState(0);

  // Return dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnItem, setReturnItem] = useState(null);
  const [returnPlan, setReturnPlan] = useState(null);
  const [returnType, setReturnType] = useState('warehouse');
  const [returnLocation, setReturnLocation] = useState({
    warehouse_id: null,
    rack_id: null,
    bay_id: null
  });
  const [warehouses, setWarehouses] = useState([]);
  const [racks, setRacks] = useState([]);
  const [bays, setBays] = useState([]);

  // Technician checkout dialog state
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [selectedItemForCheckout, setSelectedItemForCheckout] = useState(null);
  const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState('');

  // Load existing plans for this job
  const loadPlans = useCallback(async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      
      const response = await planningApi.getJobEquipmentPlans(jobId, { 
        _t: Date.now() // Cache busting parameter
      });
      
      
      // Handle both response.data.data and response.data formats
      let plansData = response.data?.data || response.data || [];
      
      if (!Array.isArray(plansData)) {
        console.error(`[EQUIPMENT PLANNING DEBUG] ❌ Plans data is not an array:`, typeof plansData, plansData);
        plansData = [];
      }
      
      
      // Filter plans for site-specific mode (include job-level plans)
      if (siteId) {
        plansData = plansData.filter(plan => {
          const planSiteId = plan.siteId || plan.site_id;
          const shouldInclude = planSiteId === siteId || planSiteId === siteId.toString() || planSiteId === 'job';
          return shouldInclude;
        });
      } else {
      }

      // Backend now handles unplanned equipment detection
      // No need to fetch assigned equipment separately

      setPlans(plansData);

      // Preload individual items for all plans so checked out counts show correctly on page load
      for (const plan of plansData) {
        await loadIndividualItems(plan.id);
      }
    } catch (error) {
      console.error('Error loading equipment plans:', error);
      // Show user-friendly error message
      enqueueSnackbar('Failed to load equipment plans. Please refresh the page.', { 
        variant: 'error',
        autoHideDuration: 5000 
      });
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [jobId, siteId]); // Include siteId to refetch when site changes

  // Load sites for this job
  const loadSites = useCallback(async () => {
    if (!jobId) return;

    try {
      const response = await jobApi.getJobSites(jobId);
      
      const sitesData = response.data?.data || response.data || [];
      
      let sitesWithJobOption = [];
      
      // If we're in site-specific mode, only show that site
      if (siteId) {
        const currentSite = sitesData.find(site => site.id === siteId);
        if (currentSite) {
          sitesWithJobOption = [{
            id: currentSite.id,
            name: currentSite.name,
            location: currentSite.location || currentSite.description || 'Job site',
            description: currentSite.description || ''
          }];
        } else {
          // Fallback if site not found
          sitesWithJobOption = [{ id: siteId, name: 'Current Site', location: 'This site', description: '' }];
        }
      } else {
        // Job-level planning: show job option + all sites
        sitesWithJobOption = [
          { id: 'job', name: 'Job Level', location: 'Equipment assigned to entire job', description: 'Can be used at any site for this job' }
        ];
        
        // Add actual sites if they exist
        if (Array.isArray(sitesData) && sitesData.length > 0) {
          sitesData.forEach(site => {
            sitesWithJobOption.push({
              id: site.id,
              name: site.name,
              location: site.location || site.description || 'Job site',
              description: site.description || ''
            });
          });
        }
      }
      
      setSites(sitesWithJobOption);
      setSelectedSite(sitesWithJobOption[0]); // Default to first option
    } catch (error) {
      console.error('Error loading sites:', error);
      // Fallback based on mode
      if (siteId) {
        setSites([{ id: siteId, name: 'Current Site', location: 'This site', description: '' }]);
        setSelectedSite({ id: siteId, name: 'Current Site', location: 'This site', description: '' });
      } else {
        setSites([{ id: 'job', name: 'Job Level', location: 'Equipment assigned to entire job', description: 'Can be used at any site for this job' }]);
        setSelectedSite({ id: 'job', name: 'Job Level', location: 'Equipment assigned to entire job', description: 'Can be used at any site for this job' });
      }
    }
  }, [jobId, siteId]);

  // Load equipment library for search - Use planning API + templates for comprehensive search
  const loadEquipmentLibrary = useCallback(async (search = '') => {
    try {

      // Get ONLY templates (not inventory items) for planning
      const templatesResponse = await equipmentService.getTemplates({ search, limit: 50 });

      // Transform templates data - templatesResponse is already response.data from the service
      // The API returns { success: true, data: [...] }
      const templatesArray = Array.isArray(templatesResponse) ? templatesResponse : (templatesResponse?.data || []);
      const templatesData = templatesArray.map(template => ({
        id: `template_${template.id}`,
        equipment_id: `template_${template.id}`,
        description: template.template_data?.description || `${template.template_data?.manufacturer || ''} ${template.template_data?.model || ''}`.trim(),
        manufacturer: template.template_data?.manufacturer || '',
        model: template.template_data?.model || '',
        available_quantity: 0, // Templates don't have quantities
        isTemplate: true,
        templateId: template.id,
        barcode_pattern: template.barcode_pattern || null, // Include the barcode_pattern field
        templateName: template.name,
        barcodePattern: template.barcode_pattern,
        templateData: template.template_data
      }));

      // Use only templates (no inventory items needed for planning)
      const equipmentData = templatesData;
      
      if (equipmentData.length === 0) {
        console.error('❌ No equipment items or templates found');
      } else {
      }
      
      const library = equipmentData.map(item => ({
        equipment_id: item.equipment_id || item.id,
        description: item.description || 'Unknown Equipment',
        manufacturer: item.manufacturer || '',
        model: item.model || '',
        available_quantity: item.available_quantity || item.total_quantity || 0,
        // Preserve template-specific properties
        isTemplate: item.isTemplate || false,
        templateId: item.templateId,
        templateName: item.templateName,
        barcodePattern: item.barcodePattern,
        templateData: item.templateData
      }));

      // All items are already templates, no need to filter
      console.log('🔍 [EQUIPMENT SEARCH] Templates loaded:', library.length, library);
      setEquipmentLibrary(library);
    } catch (error) {
      console.error('🔍 [TEMPLATE LOADING] ❌ Failed to load equipment library:', error);
      console.error('🔍 [TEMPLATE LOADING] Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Let's test if the planning API endpoint is working at all
      try {
        const testResponse = await fetch('/api/plans/library');
        const testData = await testResponse.json();
      } catch (testError) {
        console.error('🔍 [TEMPLATE LOADING] Direct API test failed:', testError);
      }
      
      // Fallback to basic mock data for demo purposes
      const fallbackLibrary = [
        { equipment_id: 'QSC-K12.2', description: 'QSC K12.2 Active Speaker', manufacturer: 'QSC', model: 'K12.2', available_quantity: 8 }
      ];
      
      const filtered = search.length >= 2 
        ? fallbackLibrary.filter(item => 
            item.description.toLowerCase().includes(search.toLowerCase()) ||
            item.manufacturer.toLowerCase().includes(search.toLowerCase()) ||
            item.model.toLowerCase().includes(search.toLowerCase())
          )
        : fallbackLibrary;
        
      setEquipmentLibrary(filtered);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadPlans();
    loadEquipmentLibrary();
    loadSites();
    loadCategories();
  }, [jobId, siteId]); // Reload when jobId or siteId changes

  // Listen for equipment status changes from Quick Actions or other components
  useEffect(() => {
    const handleEquipmentChange = (event) => {
      console.log('🔄 JobEquipmentPlanning received equipmentStatusChanged event:', event.detail);
      console.log('   Reloading equipment plans and refreshing activity timeline...');
      // Reload plans to show updated equipment counts and status
      loadPlans();
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('equipmentStatusChanged', handleEquipmentChange);
    console.log('✅ JobEquipmentPlanning: Listening for equipment status changes (jobId:', jobId, ')');

    return () => {
      window.removeEventListener('equipmentStatusChanged', handleEquipmentChange);
    };
  }, [loadPlans]); // Include loadPlans in dependencies

  // Load equipment categories
  const loadCategories = async () => {
    // Categories endpoint not implemented yet - disabled for now
    // try {
    //   const response = await api.get('/equipment-categories');
    //   if (response.data?.success && response.data?.data) {
    //     setCategories(response.data.data);
    //   }
    // } catch (error) {
    //   console.log('Categories not loaded - feature may be disabled:', error);
    //   // Don't show error to user as categories are optional
    // }
  };

  // Handle equipment search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText.length > 2) {
        loadEquipmentLibrary(searchText);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, loadEquipmentLibrary]);

  // Auto-select newly created template when library updates
  useEffect(() => {
    if (pendingTemplateSelection && equipmentLibrary.length > 0) {
      
      const newlyCreatedTemplate = equipmentLibrary.find(item => 
        item.isTemplate && 
        item.manufacturer === pendingTemplateSelection.manufacturer && 
        item.model === pendingTemplateSelection.model
      );
      
      if (newlyCreatedTemplate) {
        setSelectedEquipment(newlyCreatedTemplate);
        setPlannedQuantity(1);
        setPendingTemplateSelection(null); // Clear pending selection
      } else {
      }
    }
  }, [equipmentLibrary, pendingTemplateSelection]);

  // Load technicians on mount
  useEffect(() => {
    const loadTechnicians = async () => {
      try {
        const response = await api.get('/auth/technicians');
        const techList = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setTechnicians(techList);
      } catch (err) {
        console.error('Error loading technicians:', err);
        setTechnicians([]);
      }
    };
    loadTechnicians();
  }, []);

  // Open add equipment dialog
  const handleOpenAddDialog = () => {
    setSelectedEquipment(null);
    setPlannedQuantity(1);
    setSearchText('');
    setSelectedSite(sites[0] || null); // Reset to default site
    setAddDialog(true);
  };

  // Close add equipment dialog
  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setSelectedEquipment(null);
    setPlannedQuantity(1);
    setSearchText('');
  };

  // Add equipment to plan
  const handleAddEquipment = async () => {
    if (!selectedEquipment || plannedQuantity < 1) {
      enqueueSnackbar('Please select equipment and enter quantity', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      
      // Check if equipment already planned
      const existingPlan = plans.find(p => p.equipment_id === selectedEquipment.equipment_id);
      if (existingPlan) {
        enqueueSnackbar('Equipment already planned for this job', { variant: 'warning' });
        return;
      }

      const planData = {
        plans: [{
          equipmentId: selectedEquipment.equipment_id,
          neededQuantity: plannedQuantity,
          orderStatus: 'NOT_ORDERED',
          siteId: selectedSite?.id || null,
          siteName: selectedSite?.name || 'Job Level',
          // Include equipment details for new equipment
          description: selectedEquipment.description,
          manufacturer: selectedEquipment.manufacturer,
          model: selectedEquipment.model,
          serial_number: selectedEquipment.serial_number,
          barcode: selectedEquipment.barcode_pattern || selectedEquipment.barcodePattern || selectedEquipment.barcode || null
        }]
      };

      const response = await planningApi.createEquipmentPlan(jobId, planData);
      
      // Store equipment name before resetting form
      const equipmentName = selectedEquipment.description;
      
      // Immediately refresh plans without delay to prevent race conditions
      await loadPlans();
      
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
      
      // Reset form but keep dialog open for additional equipment
      setSelectedEquipment(null);
      setPlannedQuantity(1);
      setSearchText('');
      setSelectedSite(sites[0] || null);
      
      enqueueSnackbar(`${equipmentName} added to plan. Add more equipment or close when done.`, { variant: 'success' });
      
    } catch (error) {
      console.error('Error adding equipment to plan:', error);
      enqueueSnackbar('Failed to add equipment to plan', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Start editing quantity and site
  const handleStartEdit = (plan) => {
    setEditingId(plan.id);
    setEditQuantity(plan.needed_quantity);
    // Find the matching site object
    const matchingSite = sites.find(site => 
      site.name === (plan.siteName || plan.site_name) || 
      site.id === (plan.siteId || plan.site_id)
    ) || sites[0]; // Default to first site if not found
    setEditSite(matchingSite);
  };

  // Add note to equipment plan
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedPlan) {
      enqueueSnackbar('Please enter a note', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      
      // Create timestamped note with user info
      const timestamp = new Date().toLocaleString();
      const user = 'System User'; // TODO: Get actual user name
      const noteWithTimestamp = `[${timestamp}] ${user}: ${newNote.trim()}`;
      
      // Append to existing notes or create new notes
      const updatedNotes = selectedPlan.notes 
        ? `${selectedPlan.notes}\n${noteWithTimestamp}`
        : noteWithTimestamp;

      // Update the plan with new notes
      await planningApi.updateEquipmentPlan(selectedPlan.id, {
        notes: updatedNotes
      });

      // Create activity log entry so it shows up in Planning Activity timeline
      try {
        await api.post('/activity/log', {
          type: 'NOTE_ADDED',
          description: `Added note to ${selectedPlan.description}: "${newNote.trim()}"`,
          jobId: jobId
        });
      } catch (activityError) {
        console.error('Error creating activity log:', activityError);
        // Don't fail the whole operation if activity logging fails
      }

      enqueueSnackbar('Note added successfully', { variant: 'success' });
      
      // Close dialog and refresh plans
      setNotesDialog(false);
      setNewNote('');
      setSelectedPlan(null);
      await loadPlans();
      
      // Refresh activity timeline to show the notes update
      setActivityRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error adding note:', error);
      enqueueSnackbar('Failed to add note', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Save quantity and site edit
  const handleSaveEdit = async () => {
    if (editQuantity < 1) {
      enqueueSnackbar('Quantity must be at least 1', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      
      const updateData = {
        neededQuantity: editQuantity,
        siteId: editSite?.id || null,
        siteName: editSite?.name || 'Job Level'
      };

      await planningApi.updateEquipmentPlan(editingId, updateData);
      enqueueSnackbar('Plan updated successfully', { variant: 'success' });
      
      // Refresh plans
      await loadPlans();
      
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
      
      setEditingId(null);
      setEditQuantity(0);
      setEditSite(null);
      
    } catch (error) {
      console.error('Error updating plan:', error);
      enqueueSnackbar('Failed to update plan', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle row expansion/collapse
  const handleRowToggle = (planId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(planId)) {
      newExpanded.delete(planId);
    } else {
      newExpanded.add(planId);
      // Load individual items for this plan if not already loaded
      if (!individualItems[planId]) {
        loadIndividualItems(planId);
      }
    }
    setExpandedRows(newExpanded);
  };

  // Load individual equipment items for a plan
  const loadIndividualItems = async (planId) => {
    try {
      
      // For unplanned items, we need to pass the jobId as a query parameter
      let response;
      if (planId.toString().startsWith('unplanned_')) {
        response = await api.get(`/plans/${planId}/items?jobId=${jobId}`);
      } else {
        response = await planningApi.getPlanEquipmentItems(planId);
      }
      
      const items = response.data?.data || response.data || [];

      
      setIndividualItems(prev => ({
        ...prev,
        [planId]: items
      }));
    } catch (error) {
      console.error('Error loading individual items:', error);
      
      // Only show error for non-unplanned items
      if (!planId.toString().startsWith('unplanned_')) {
        enqueueSnackbar('Failed to load equipment items', { variant: 'error' });
      }
      
      // Set empty array on error - no fallback items
      setIndividualItems(prev => ({
        ...prev,
        [planId]: []
      }));
    }
  };

  // Update individual item status
  const updateItemStatus = async (planId, itemId, newStatus) => {
    try {
      // Update local state immediately for responsive UI
      setIndividualItems(prev => ({
        ...prev,
        [planId]: prev[planId].map(item =>
          item.id === itemId ? { ...item, status: newStatus } : item
        )
      }));

      // All items now have real database IDs, so persist the change
      await equipmentApi.updateStatus(itemId, {
        status: newStatus,
        notes: `Status updated from Equipment Planning - ${newStatus}`
      });
      
      enqueueSnackbar(`Equipment status updated to ${newStatus}`, { variant: 'success' });
    } catch (error) {
      console.error('Error updating equipment status:', error);
      enqueueSnackbar('Failed to update equipment status', { variant: 'error' });
      
      // Revert local state change on error
      const originalItem = individualItems[planId]?.find(i => i.id === itemId);
      setIndividualItems(prev => ({
        ...prev,
        [planId]: prev[planId].map(item =>
          item.id === itemId ? { ...item, status: originalItem?.status || 'planned' } : item
        )
      }));
    }
  };

  // Calculate assigned quantity for a plan based on individual item statuses
  const getAssignedQuantity = (planId) => {
    const items = individualItems[planId];
    if (!items) return 0;
    // Count items that have been received for this job (assigned, checked_out, or installed)
    // "Received" means items that have been received and designated for this job/site
    // regardless of whether they're currently assigned, checked out to a technician, or installed
    return items.filter(item =>
      item.status === 'assigned' ||
      item.status === 'checked_out' ||
      item.status === 'installed'
    ).length;
  };

  // Calculate checked out quantity for a plan based on individual item statuses
  const getCheckedOutQuantity = (planId) => {
    const items = individualItems[planId];
    if (!items) return 0;
    // Count items that are checked out
    return items.filter(item => item.status === 'checked_out').length;
  };

  // Handle viewing equipment history
  const handleViewHistory = (item, plan) => {
    // Build equipment object with all necessary fields for StatusHistoryDialog
    const equipmentData = {
      id: item.id,
      description: plan.description,
      status: item.status,
      manufacturer: plan.manufacturer,
      model: plan.model,
      serialNumber: item.serialNumber,
      current_job_id: jobId,
      warehouse: item.warehouse,
      rack: item.rack || item.zone,
      bay: item.bay
    };

    setSelectedEquipmentForHistory(equipmentData);
    setHistoryDialogOpen(true);
  };

  // Handle printing QR code
  const handlePrintQRCode = async (item, plan) => {
    const itemWithJobInfo = {
      ...item,
      id: item.id,
      equipment_id: item.equipment_id || item.id,
      description: plan.description,
      manufacturer: plan.manufacturer,
      model: plan.model,
      serial_number: item.serial_number || item.serialNumber,
      job_id: jobId,
      job_number: plan.job_number,
      site_id: item.site_id || siteId,
      site_name: item.site_name
    };
    await printSingleQRCode(itemWithJobInfo);
  };

  // Handle marking item as installed (handles proper status transitions)
  const handleMarkInstalled = async (item, plan) => {
    try {
      setSaving(true);
      // Get the user email from auth context or local storage
      const userEmail = localStorage.getItem('userEmail') || 'Unknown User';

      // Determine the next status based on current status
      // Valid workflow: assigned/returned → checked_out → installed
      let nextStatus;
      let successMessage;

      switch(item.status) {
        case 'assigned':
        case 'returned':
          // Open checkout dialog to select technician
          setSaving(false); // Don't keep saving indicator during dialog
          setSelectedItemForCheckout(item);
          setSelectedPlanForCheckout(plan);
          setSelectedTechnician('');
          setCheckoutDialogOpen(true);
          return; // Exit - actual checkout happens in dialog handler
        case 'checked_out':
          nextStatus = 'installed';
          successMessage = 'Equipment marked as installed at job site';
          break;
        case 'installed':
          enqueueSnackbar('Equipment is already installed', { variant: 'info' });
          return;
        case 'available':
          enqueueSnackbar('Equipment must be assigned to job before installation. Use the Assign to Job function first.', { variant: 'warning' });
          return;
        default:
          enqueueSnackbar(`Cannot install equipment with status "${item.status}". Equipment must be assigned or checked out first.`, { variant: 'warning' });
          return;
      }

      // The backend expects these exact fields for status update
      await equipmentApi.updateEquipmentItem(item.id, {
        status: nextStatus,
        updated_by: userEmail
      });

      enqueueSnackbar(successMessage, { variant: 'success' });

      // Reload the plan items
      const planId = plan.id || plans.find(p =>
        p.manufacturer === item.manufacturer &&
        p.model === item.model
      )?.id;

      // Force clear the cached items for this plan to ensure fresh data
      if (planId) {
        setIndividualItems(prev => ({
          ...prev,
          [planId]: undefined
        }));
      }

      if (planId) {
        await loadIndividualItems(planId);
      }
      // Refresh plans to update counts
      await loadPlans();
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error updating equipment status:', error);

      // Provide detailed error messages
      if (error.message?.includes('Invalid Status Transition')) {
        enqueueSnackbar(
          'Status transition not allowed. Equipment must follow this workflow: Assigned → Checked Out → Installed. ' +
          `Current status: ${item.status}`,
          { variant: 'error' }
        );
      } else if (error.message?.includes('Invalid status')) {
        enqueueSnackbar(
          `Cannot update to this status. Valid statuses are: available, received, assigned, checked_out, installed, returned, maintenance, archived`,
          { variant: 'error' }
        );
      } else {
        enqueueSnackbar(
          `Failed to update equipment status: ${error.message || 'Unknown error occurred'}`,
          { variant: 'error' }
        );
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle checkout dialog submission
  const handleCheckoutSubmit = async () => {
    if (!selectedTechnician) {
      enqueueSnackbar('Please select a technician', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const userEmail = localStorage.getItem('userEmail') || 'Unknown User';
      const userId = localStorage.getItem('userId') || null;

      // Update equipment with checkout information
      await equipmentApi.updateEquipmentItem(selectedItemForCheckout.id, {
        status: 'checked_out',
        checked_out_to: selectedTechnician,
        checked_out_by: userId,
        checked_out_at: new Date().toISOString(),
        updated_by: userEmail
      });

      enqueueSnackbar(`Equipment checked out to ${selectedTechnician}. Click again to mark as installed.`, { variant: 'success' });

      // Get plan ID before closing dialog
      const planId = selectedPlanForCheckout.id || plans.find(p =>
        p.manufacturer === selectedItemForCheckout.manufacturer &&
        p.model === selectedItemForCheckout.model
      )?.id;

      // Close dialog
      setCheckoutDialogOpen(false);
      setSelectedItemForCheckout(null);
      setSelectedPlanForCheckout(null);
      setSelectedTechnician('');

      // Force clear the cached items for this plan to ensure fresh data
      if (planId) {
        setIndividualItems(prev => ({
          ...prev,
          [planId]: undefined
        }));
      }

      // Reload the plan items with fresh data
      if (planId) {
        await loadIndividualItems(planId);
      }
      // Refresh plans to update counts
      await loadPlans();
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error checking out equipment:', error);
      enqueueSnackbar(`Failed to checkout equipment: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Open return dialog
  const openReturnDialog = (item, plan) => {
    setReturnItem(item);
    setReturnPlan(plan);
    setReturnType('job'); // Default to 'Return to Job'
    setReturnLocation({ warehouse_id: null, rack_id: null, bay_id: null });
    setReturnDialogOpen(true);
    // Load all location options (they're independent now)
    loadWarehouses();
    loadRacks();
    loadBays();
  };

  // Load warehouse options
  const loadWarehouses = async () => {
    try {
      const response = await api.get('/inventories/warehouses');
      // The API returns { success: true, data: { warehouses: [...] } }
      const warehouseData = response.data?.data?.warehouses || response.data?.data || [];
      setWarehouses(warehouseData);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setWarehouses([]);
    }
  };

  // Load racks - now independent, not filtered by warehouse
  const loadRacks = async () => {
    try {
      const response = await api.get('/inventories/racks');
      // The API returns { success: true, data: [...] }
      const rackData = response.data?.data || [];
      setRacks(rackData);
    } catch (error) {
      console.error('Error loading racks:', error);
      setRacks([]);
    }
  };

  // Load bays - now independent, not filtered by rack
  const loadBays = async () => {
    try {
      const response = await api.get('/inventories/bays');
      // The API returns { success: true, data: [...] }
      const bayData = response.data?.data || [];
      setBays(bayData);
    } catch (error) {
      console.error('Error loading bays:', error);
      setBays([]);
    }
  };

  // Handle returning equipment
  const handleReturnEquipment = async () => {
    try {
      setSaving(true);
      const userEmail = localStorage.getItem('userEmail') || 'Unknown User';

      let updateData = {
        updated_by: userEmail
      };

      if (returnType === 'warehouse') {
        // Return to warehouse (make available)
        updateData.status = 'returned';
        updateData.current_job_id = null;
        // Set warehouse location if provided
        if (returnLocation.warehouse_id) {
          updateData.warehouse_id = returnLocation.warehouse_id;
        }
        if (returnLocation.rack_id) {
          updateData.rack_id = returnLocation.rack_id;
        }
        if (returnLocation.bay_id) {
          updateData.bay_id = returnLocation.bay_id;
        }

        // Single update for warehouse return
        await equipmentApi.updateEquipmentItem(returnItem.id, updateData);
      } else {
        // Return to job (status: returned)
        // Keep the item assigned to the job but mark as returned to job site
        updateData.status = 'returned';
        // Keep current_job_id - the item stays with the job
        await equipmentApi.updateEquipmentItem(returnItem.id, updateData);

        // CRITICAL: Also decrement the plan's installed_quantity to prevent auto-sync from marking it back as installed
        if (returnPlan?.id && returnPlan.installed_quantity > 0) {
          console.log('[RETURN DEBUG] Decrementing installed_quantity for plan:', returnPlan.id, 'from', returnPlan.installed_quantity);
          await planningApi.updateEquipmentPlan(returnPlan.id, {
            installedQuantity: returnPlan.installed_quantity - 1
          });
        }
      }

      enqueueSnackbar(
        returnType === 'warehouse'
          ? 'Equipment returned to warehouse and available for reassignment.'
          : 'Equipment returned and re-assigned to job. Ready for checkout.',
        { variant: 'success' }
      );

      // Close dialog
      setReturnDialogOpen(false);

      // Reload the plan items - improved planId lookup with logging
      console.log('[RETURN DEBUG] Reloading items after return:', {
        returnPlanId: returnPlan?.id,
        returnItemManufacturer: returnItem.manufacturer,
        returnItemModel: returnItem.model
      });

      let planId = returnPlan?.id;
      if (!planId) {
        // Try to find plan by manufacturer and model
        const matchingPlan = plans.find(p =>
          p.manufacturer === returnItem.manufacturer &&
          p.model === returnItem.model
        );
        planId = matchingPlan?.id;
        console.log('[RETURN DEBUG] Found matching plan by manufacturer/model:', matchingPlan);
      }

      // Force clear the cached items for this plan to ensure fresh data
      if (planId) {
        console.log('[RETURN DEBUG] Refreshing items for plan:', planId);
        setIndividualItems(prev => ({
          ...prev,
          [planId]: undefined
        }));
        await loadIndividualItems(planId);
      } else {
        console.warn('[RETURN DEBUG] Could not find planId to refresh items!');
        // Force refresh all plans which will reload all expanded rows
        await loadPlans();
        // Manually reload items for all expanded rows
        // Clear cache for all expanded rows
        setIndividualItems(prev => {
          const newItems = { ...prev };
          for (const expandedPlanId of expandedRows) {
            newItems[expandedPlanId] = undefined;
          }
          return newItems;
        });
        for (const expandedPlanId of expandedRows) {
          await loadIndividualItems(expandedPlanId);
        }
      }

      // Refresh plans to update counts
      await loadPlans();
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error returning equipment:', error);
      enqueueSnackbar(
        `Failed to return equipment: ${error.message || 'Unknown error occurred'}`,
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle printing QR code label for item
  const handlePrintLabel = (item) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      enqueueSnackbar('Please allow popups to print labels', { variant: 'error' });
      return;
    }
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Equipment Label - ${item.equipment_id}</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .label { text-align: center; }
            .equipment-id { font-size: 18px; font-weight: bold; margin: 10px 0; }
            .details { font-size: 14px; margin: 5px 0; }
          </style>
        </head>
        <body>
          <div class="label">
            <div id="qrcode"></div>
            <div class="equipment-id">${item.equipment_id || `EQ-${item.id}`}</div>
            <div class="details">${item.manufacturer || ''} ${item.model || ''}</div>
            <div class="details">SN: ${item.serialNumber || 'N/A'}</div>
          </div>
          <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.1/build/qrcode.min.js"></script>
          <script>
            QRCode.toCanvas(document.createElement('canvas'), '${item.equipment_id || item.id}', { width: 200 }, function (error, canvas) {
              if (!error) {
                document.getElementById('qrcode').appendChild(canvas);
                setTimeout(() => { window.print(); window.close(); }, 500);
              }
            });
          </script>
        </body>
      </html>
    `);
  };
  
  // Handle opening item notes dialog
  const handleOpenItemNotesDialog = (item, plan) => {
    setSelectedItemForNotes(item);
    setSelectedPlanForItemNotes(plan);
    setNewItemNote('');
    setItemNotesDialog(true);
  };
  
  // Handle adding note to item
  const handleAddItemNote = async () => {
    if (!selectedItemForNotes || !newItemNote.trim()) return;
    
    try {
      setSaving(true);
      const existingNotes = selectedItemForNotes.notes || '';
      const updatedNotes = existingNotes 
        ? `${existingNotes}\n${new Date().toLocaleString()}: ${newItemNote}`
        : `${new Date().toLocaleString()}: ${newItemNote}`;
      
      // Update the item with new notes
      await equipmentApi.updateEquipmentItem(selectedItemForNotes.id, {
        notes: updatedNotes
      });
      
      // Update local state
      setIndividualItems(prev => ({
        ...prev,
        [selectedPlanForItemNotes.id]: prev[selectedPlanForItemNotes.id].map(i =>
          i.id === selectedItemForNotes.id ? { ...i, notes: updatedNotes } : i
        )
      }));
      
      // Also update the selected item for notes
      setSelectedItemForNotes({ ...selectedItemForNotes, notes: updatedNotes });
      
      enqueueSnackbar('Note added successfully', { variant: 'success' });
      setNewItemNote('');
      
      // Refresh the plans to update aggregated notes
      await loadPlans();
    } catch (error) {
      console.error('Error adding note:', error);
      enqueueSnackbar('Failed to add note', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle item field changes (vendor, PO, notes)
  const handleItemFieldChange = (planId, itemId, field, value) => {
    setIndividualItems(prev => ({
      ...prev,
      [planId]: prev[planId].map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  // Save item field to backend
  const handleSaveItemField = async (itemId, field, value) => {
    try {
      await equipmentApi.updateEquipmentItem(itemId, {
        [field]: value
      });
      // Success - value is already updated in state from handleItemFieldChange
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      enqueueSnackbar(`Failed to update ${field}`, { variant: 'error' });
      // Reload plans to revert to saved value
      await loadPlans();
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditQuantity(0);
    setEditSite(null);
  };

  // Delete plan
  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Remove ${plan.description} from job plan?`)) {
      return;
    }

    try {
      setSaving(true);
      await planningApi.deleteEquipmentPlan(jobId, plan.id);
      enqueueSnackbar('Equipment removed from plan', { variant: 'success' });
      await loadPlans();
      
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting plan:', error);
      enqueueSnackbar('Failed to remove equipment', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Order dialog handlers
  const handleOpenOrderDialog = (plan) => {
    setSelectedPlanForOrder(plan);
    // Stock (availableQuantity) is NOT subtracted - it's kept as emergency/reserve inventory
    // Only subtract what's already been ordered and assigned for this specific job
    const stillNeeded = Math.max(0, (plan.needed_quantity || 0) - (plan.orderedQuantity || 0) - getAssignedQuantity(plan.id));

    // Hybrid approach: If item has existing order data, show it (editing mode)
    // Otherwise, start fresh but keep session notes for convenience (new order mode)
    const hasExistingOrder = plan.vendor_name || plan.order_reference || plan.order_notes;

    setOrderFormData(prev => ({
      orderedQuantity: stillNeeded,
      orderDate: plan.order_date || new Date().toISOString().split('T')[0],
      vendor: plan.vendor_name || '', // Use item's saved vendor, or blank
      orderReference: plan.order_reference || '', // Use item's saved PO, or blank
      notes: plan.order_notes || '' // Use item's notes only, don't carry over from previous items
    }));
    setOrderDialogOpen(true);
  };

  const handleCloseOrderDialog = () => {
    setOrderDialogOpen(false);
    setSelectedPlanForOrder(null);
    // Reset order-specific fields but preserve notes for convenience
    setOrderFormData(prev => ({
      orderedQuantity: 0,
      orderDate: new Date().toISOString().split('T')[0],
      vendor: '',
      orderReference: '',
      notes: '' // Clear notes - should be item-specific, not shared
    }));
  };

  const handleSubmitOrder = async () => {
    if (!selectedPlanForOrder) return;

    try {
      setSaving(true);

      // Update the plan with order information
      const updateData = {
        orderedQuantity: parseInt(orderFormData.orderedQuantity, 10),
        orderDate: orderFormData.orderDate,
        vendor: orderFormData.vendor,
        orderReference: orderFormData.orderReference,
        orderNotes: orderFormData.notes
      };

      await planningApi.updateEquipmentPlan(selectedPlanForOrder.id, updateData);

      // Activity logging is handled by the backend when updating the plan
      // No need to log separately from the frontend

      enqueueSnackbar(
        `Ordered ${orderFormData.orderedQuantity} ${selectedPlanForOrder.description} for ${orderFormData.orderDate}`,
        { variant: 'success' }
      );

      // Reload plans to show updated data
      await loadPlans();

      // Refresh activity timeline to show the new activity
      setActivityRefreshTrigger(prev => prev + 1);

      handleCloseOrderDialog();
    } catch (error) {
      console.error('Error updating order:', error);
      enqueueSnackbar('Failed to update order information', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle opening status change menu
  const handleOpenStatusMenu = (event, item) => {
    setStatusMenuAnchor(event.currentTarget);
    setStatusMenuSelectedItem(item);
  };

  // Handle closing status menu
  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuSelectedItem(null);
  };

  // Handle status change from menu
  const handleStatusChange = async (newStatus) => {
    if (!statusMenuSelectedItem) return;

    try {
      setSaving(true);
      const userEmail = localStorage.getItem('userEmail') || 'Unknown User';

      // Update equipment status
      await equipmentApi.updateEquipmentItem(statusMenuSelectedItem.id, {
        status: newStatus,
        updated_by: userEmail
      });

      // Get status label for feedback
      const statusLabel = EQUIPMENT_STATUS[newStatus]?.label || newStatus;
      enqueueSnackbar(`Equipment status changed to ${statusLabel}`, { variant: 'success' });

      // Find the plan this item belongs to
      const planId = Object.keys(individualItems).find(pid =>
        individualItems[pid]?.some(item => item.id === statusMenuSelectedItem.id)
      );

      // Force clear the cached items for this plan to ensure fresh data
      if (planId) {
        setIndividualItems(prev => ({
          ...prev,
          [planId]: undefined
        }));
        await loadIndividualItems(planId);
      }

      // Refresh plans to update counts
      await loadPlans();

      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);

      handleCloseStatusMenu();
    } catch (error) {
      console.error('Error changing equipment status:', error);

      // Provide detailed error message
      if (error.response?.data?.error) {
        enqueueSnackbar(error.response.data.error, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to change equipment status', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOrderFormChange = (field, value) => {
    setOrderFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Installed dialog handlers
  const handleOpenInstalledDialog = (plan) => {
    setSelectedPlanForInstalled(plan);
    setInstalledFormData({
      installedQuantity: plan.installed_quantity || 0,
      notes: ''
    });
    setInstalledDialogOpen(true);
  };

  const handleCloseInstalledDialog = () => {
    setInstalledDialogOpen(false);
    setSelectedPlanForInstalled(null);
    setInstalledFormData({
      installedQuantity: 0,
      notes: ''
    });
  };

  const handleSubmitInstalled = async () => {
    if (!selectedPlanForInstalled) return;

    // Check if this is an unplanned item
    if (selectedPlanForInstalled.id?.toString().startsWith('unplanned_')) {
      enqueueSnackbar('Cannot update unplanned items - they are not assigned to this job yet', { variant: 'warning' });
      handleCloseInstalledDialog();
      return;
    }

    try {
      setSaving(true);
      
      // Update the plan with installed information
      const updateData = {
        installedQuantity: parseInt(installedFormData.installedQuantity, 10)
      };

      await planningApi.updateEquipmentPlan(selectedPlanForInstalled.id, updateData);
      
      enqueueSnackbar('Installed quantity updated', { variant: 'success' });
      await loadPlans();
      
      // Refresh activity timeline
      setActivityRefreshTrigger(prev => prev + 1);
      
      handleCloseInstalledDialog();
    } catch (error) {
      console.error('Error updating installed quantity:', error);
      enqueueSnackbar('Failed to update installed quantity', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleInstalledFormChange = (field, value) => {
    setInstalledFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate summary statistics
  const summary = plans.reduce((acc, plan) => ({
    totalPlanned: acc.totalPlanned + (plan.needed_quantity || 0),
    totalAssigned: acc.totalAssigned + (plan.assigned_quantity || plan.assignedQuantity || getAssignedQuantity(plan.id) || 0),
    typesPlanned: acc.typesPlanned + 1
  }), { totalPlanned: 0, totalAssigned: 0, typesPlanned: 0 });

  const completionPercentage = summary.totalPlanned > 0 
    ? Math.round((summary.totalAssigned / summary.totalPlanned) * 100) 
    : 0;

  // Format equipment option for autocomplete
  const formatEquipmentOption = (option) => {
    if (!option) return '';
    return `${option.manufacturer || ''} ${option.model || ''} - ${option.description || ''}`.trim();
  };

  // Template management handlers
  const handleCreateTemplate = () => {
    
    if (!searchText?.trim()) {
      enqueueSnackbar('Please enter equipment details first', { variant: 'warning' });
      return;
    }

    // Parse manufacturer and model from search text
    // If only one word, use it as both manufacturer and model placeholder
    const searchParts = searchText.trim().split(' ');
    const manufacturer = searchParts[0] || '';
    const model = searchParts.slice(1).join(' ') || '';

    setTemplateFormData({
      name: searchText.trim(),
      manufacturer: manufacturer,
      model: model || '', // Empty model is OK, user can fill it in the form
      description: searchText.trim(),
      categoryId: '',
      barcodePattern: '',
      notes: ''
    });
    
    // Show the inline template form
    setShowTemplateForm(true);
  };

  // Open barcode scanner dialog
  const openBarcodeScanner = (callback) => {
    setScannerCallback(() => callback);
    setScannerDialogOpen(true);
  };

  // Handle barcode scan result
  const handleBarcodeScanResult = (scannedBarcode) => {
    setScannerDialogOpen(false);
    if (scannerCallback && scannedBarcode) {
      scannerCallback(scannedBarcode);
    }
    setScannerCallback(null);
  };

  const handleScanBarcodeForTemplate = async () => {
    openBarcodeScanner(async (scannedBarcode) => {
      try {
        if (selectedEquipment?.isTemplate && selectedEquipment.templateId) {
          // Update existing template with barcode - using correct field name
          await equipmentService.updateTemplate(selectedEquipment.templateId, {
            barcode_pattern: scannedBarcode
          });

          enqueueSnackbar(`Barcode ${scannedBarcode} associated with template`, { variant: 'success' });
          
          // Refresh the equipment library to show updated template
          await loadEquipmentLibrary();
        }
      } catch (error) {
        console.error('Error scanning barcode for template:', error);
        enqueueSnackbar('Failed to associate barcode with template', { variant: 'error' });
      }
    });
  };

  const handleEditTemplate = () => {
    if (selectedEquipment?.isTemplate && selectedEquipment.templateId) {
      setTemplateFormData({
        manufacturer: selectedEquipment.manufacturer || '',
        model: selectedEquipment.model || '',
        description: selectedEquipment.description || '',
        categoryId: selectedEquipment.templateData?.category_id || '',
        barcodePattern: selectedEquipment.barcodePattern || '',
        notes: selectedEquipment.templateData?.notes || ''
      });
      setEditingTemplateId(selectedEquipment.templateId);
      setTemplateDialogOpen(true);
    }
  };

  const handleSaveTemplateInline = async () => {
    try {
      if (!templateFormData.manufacturer || !templateFormData.model || !templateFormData.description) {
        enqueueSnackbar('Please fill in all required fields (manufacturer, model, description)', { variant: 'warning' });
        return;
      }

      setSaving(true);
      
      // Generate template name from manufacturer and model
      const templateName = `${templateFormData.manufacturer} ${templateFormData.model}`.trim();
      
      const templatePayload = {
        name: templateName,
        barcodePattern: templateFormData.barcodePattern || null,
        templateData: {
          manufacturer: templateFormData.manufacturer,
          model: templateFormData.model,
          description: templateFormData.description,
          category_id: templateFormData.categoryId || null,
          notes: templateFormData.notes || null
        }
      };
      

      // Create new template
      const newTemplateResponse = await equipmentService.createTemplate(templatePayload);
      // Don't show toast here - we'll show a combined message after adding to plan

      // The response is the template object directly, not wrapped in data
      const templateData = newTemplateResponse?.id ? newTemplateResponse : newTemplateResponse?.data;

      // Track this as a newly created template (allow editing in this session)
      if (templateData?.id) {
        setNewlyCreatedTemplateIds(prev => new Set([...prev, templateData.id]));
      }
      
      // Hide the template form and clear the search immediately
      setShowTemplateForm(false);
      setSearchText('');

      // Store the template info BEFORE clearing the form
      const templateToFind = {
        manufacturer: templateFormData.manufacturer,
        model: templateFormData.model,
        name: templateName,
        id: templateData?.id
      };

      // Store the template data for adding to plan BEFORE clearing the form
      const savedTemplateData = {
        description: templateFormData.description,
        manufacturer: templateFormData.manufacturer,
        model: templateFormData.model,
        barcodePattern: templateFormData.barcodePattern
      };

      // Clear the template form data
      setTemplateFormData({
        barcodePattern: '',
        manufacturer: '',
        model: '',
        description: '',
        categoryId: '',
        notes: ''
      });

      // Reload equipment library to include the new template
      // AND automatically add the template to the job plan

      // First, reload the equipment library to get the full template data
      await loadEquipmentLibrary();

      // Now add the template to the job plan automatically
      if (templateData?.id) {
        try {
          const planData = {
            plans: [{
              equipmentId: templateData.id,
              neededQuantity: plannedQuantity || 1,
              orderStatus: 'NOT_ORDERED',
              siteId: selectedSite?.id || null,
              siteName: selectedSite?.name || 'Job Level',
              description: savedTemplateData.description,
              manufacturer: savedTemplateData.manufacturer,
              model: savedTemplateData.model,
              barcode: savedTemplateData.barcodePattern || null
            }]
          };

          await planningApi.createEquipmentPlan(jobId, planData);

          enqueueSnackbar('Template created and added to plan successfully', { variant: 'success' });

          // Refresh the plans list to show the new item
          await loadPlans();

          // Keep dialog open but reset the form so user can add more equipment
          setSelectedEquipment(null);
          setPlannedQuantity(1);
          setSearchText('');

          // Trigger activity timeline refresh
          setActivityRefreshTrigger(prev => prev + 1);
        } catch (error) {
          console.error('Error adding template to plan:', error);
          enqueueSnackbar('Template created but could not be added to plan', { variant: 'warning' });
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save template';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (!templateFormData.model || !templateFormData.description) {
        enqueueSnackbar('Please fill in all required fields (model, description)', { variant: 'warning' });
        return;
      }

      let templatePayload;
      let newTemplateResponse;
      
      if (editingTemplateId) {
        // Update existing template - PUT endpoint expects different format
        templatePayload = {
          name: `${templateFormData.manufacturer} ${templateFormData.model}`,
          manufacturer: templateFormData.manufacturer,
          model: templateFormData.model,
          description: templateFormData.description,
          barcode_pattern: templateFormData.barcodePattern || undefined,
          category: templateFormData.categoryId || undefined,
          template_data: {
            manufacturer: templateFormData.manufacturer,
            model: templateFormData.model,
            description: templateFormData.description,
            category: templateFormData.categoryId || null,
            notes: templateFormData.notes || null
          }
        };
        await equipmentService.updateTemplate(editingTemplateId, templatePayload);
        enqueueSnackbar('Template updated successfully', { variant: 'success' });
      } else {
        // Create new template - POST endpoint expects different format
        // Handle template name generation - manufacturer is optional
        const templateName = templateFormData.manufacturer
          ? `${templateFormData.manufacturer} ${templateFormData.model}`
          : templateFormData.model;

        templatePayload = {
          name: templateName,
          barcodePattern: templateFormData.barcodePattern,
          templateData: {
            manufacturer: templateFormData.manufacturer || null,
            model: templateFormData.model,
            description: templateFormData.description,
            category_id: templateFormData.categoryId || null,
            notes: templateFormData.notes
          }
        };
        newTemplateResponse = await equipmentService.createTemplate(templatePayload);
        enqueueSnackbar('Template created successfully', { variant: 'success' });
        
        // Track this as a newly created template
        if (newTemplateResponse?.data?.id) {
          setNewlyCreatedTemplateIds(prev => new Set([...prev, newTemplateResponse.data.id]));
        }
        
        // After creating the template, automatically add it to the job plan
        if (newTemplateResponse && newTemplateResponse.data) {
          const templateData = newTemplateResponse.data.data || newTemplateResponse.data;
          
          // Set the newly created template as selected equipment
          setSelectedEquipment({
            equipment_id: templateData.id,
            description: templateData.template_data?.description || templateFormData.description,
            manufacturer: templateData.template_data?.manufacturer || templateFormData.manufacturer,
            model: templateData.template_data?.model || templateFormData.model,
            name: templateData.name || templateFormData.name,
            isTemplate: true,
            templateId: templateData.id,
            templateName: templateData.name || `${templateFormData.manufacturer} ${templateFormData.model}`
          });
          
          // Reopen the add equipment dialog with the new template selected
          setPlannedQuantity(1);
          setAddDialog(true);
        }
      }

      handleCloseTemplateDialog();
      await loadEquipmentLibrary();
    } catch (error) {
      console.error('Error saving template:', error);
      enqueueSnackbar('Failed to save template', { variant: 'error' });
    }
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setEditingTemplateId(null);
    setTemplateFormData({
      name: '',
      manufacturer: '',
      model: '',
      description: '',
      categoryId: '',
      barcodePattern: '',
      notes: ''
    });
  };

  // Handle item details toggle
  const handleToggleItemDetails = (itemId) => {
    setExpandedItemDetails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };


  // Render loading state
  if (loading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading equipment plans...
          </Typography>
        </Box>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      {/* Equipment Planning Header with Progress */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Typography variant="h6">
              Equipment Planning
            </Typography>
            <Chip 
              label={`${completionPercentage}%`}
              color={completionPercentage === 100 ? 'success' : completionPercentage > 50 ? 'primary' : 'warning'}
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {summary.typesPlanned} equipment types • {summary.totalPlanned} total items • {summary.totalAssigned} assigned
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={completionPercentage}
            color={completionPercentage === 100 ? 'success' : completionPercentage > 50 ? 'primary' : 'warning'}
            sx={{ height: 6, borderRadius: 1, maxWidth: 300 }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          disabled={saving}
          sx={{ ml: 2 }}
        >
          Add Equipment
        </Button>
      </Box>

      {/* Equipment Plans Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '600px' }}>
        <Table stickyHeader sx={{ minWidth: 650 }} aria-label="equipment planning table">
          <TableHead>
            <TableRow>
              <TableCell width="50px"></TableCell>
              <TableCell width="150px">Manufacturer</TableCell>
              <TableCell width="150px">Model</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width="140px">Assigned Site</TableCell>
              <TableCell width="80px">Planned</TableCell>
              <TableCell width="80px">Stock</TableCell>
              <TableCell width="80px">Ordered</TableCell>
              <TableCell width="80px">Received</TableCell>
              <TableCell width="80px">Checked Out</TableCell>
              <TableCell width="80px">Status</TableCell>
              <TableCell width="80px">Installed</TableCell>
              <TableCell width="60px">Notes</TableCell>
              <TableCell width="100px" align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No equipment planned yet. Click "Add Equipment" to start planning.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan) => {
                const assignedQuantity = plan.assigned_quantity || plan.assignedQuantity || getAssignedQuantity(plan.id) || 0;
                const plannedQuantity = plan.needed_quantity || plan.plannedQuantity || 0;
                const stillNeeded = Math.max(0, plannedQuantity - assignedQuantity);
                const isEditing = editingId === plan.id;
                const isExpanded = expandedRows.has(plan.id);
                
                return (
                  <React.Fragment key={plan.id}>
                    <TableRow hover>
                      <TableCell>
                        {(plan.assignedQuantity > 0 || plan.assignedItems?.length > 0) ? (
                          <IconButton
                            size="small"
                            onClick={() => handleRowToggle(plan.id)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 40 }} /> // Placeholder to maintain alignment
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {plan.manufacturer || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {plan.model || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {plan.description}
                            </Typography>
                            {plan.added_during_execution && (
                              <Chip 
                                label="Added During Execution" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            )}
                            {plan.plan_source === 'auto-created' && (
                              <Chip 
                                label="Auto-Created" 
                                size="small" 
                                color="info" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Autocomplete
                          size="small"
                          options={sites}
                          getOptionLabel={(option) => option.name || 'Unknown Site'}
                          value={editSite}
                          onChange={(event, newValue) => setEditSite(newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              fullWidth
                            />
                          )}
                          sx={{ minWidth: 140 }}
                        />
                      ) : (
                        <Typography variant="body2">
                          {(() => {
                            // Look up site name using site_id (handle string/number type mismatch)
                            const siteId = plan.site_id || plan.siteId;
                            if (siteId) {
                              const site = sites.find(s => String(s.id) === String(siteId));
                              return site ? site.name : 'Unknown Site';
                            }
                            return 'Job Level';
                          })()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editQuantity}
                          onChange={(e) => setEditQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                          sx={{ width: '80px' }}
                        />
                      ) : (
                        <Chip 
                          label={plannedQuantity}
                          size="small" 
                          color={plan.added_during_execution ? 'warning' : 'default'}
                          onClick={() => handleStartEdit(plan)}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.availableQuantity || 0}
                        size="small"
                        sx={{
                          backgroundColor: (plan.availableQuantity || 0) > 0 ? EQUIPMENT_STATUS.available.color : undefined,
                          color: (plan.availableQuantity || 0) > 0 ? 'white' : undefined
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={plan.orderedQuantity || 0}
                        icon={(plan.orderedQuantity || 0) > 0 ? 
                          <LocalShippingIcon sx={{ fontSize: '1rem' }} /> : 
                          <ShoppingCartIcon sx={{ fontSize: '1rem' }} />
                        }
                        size="small"
                        color={(plan.orderedQuantity || 0) > 0 ? 'secondary' : 'default'}
                        onClick={() => handleOpenOrderDialog(plan)}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: (plan.orderedQuantity || 0) > 0 ? '#9c27b0' : undefined,
                          color: (plan.orderedQuantity || 0) > 0 ? 'white' : undefined,
                          '&:hover': {
                            backgroundColor: (plan.orderedQuantity || 0) > 0 ? '#7b1fa2' : 'action.hover',
                            transform: 'scale(1.05)',
                            boxShadow: 1
                          },
                          '&:active': {
                            transform: 'scale(0.95)'
                          },
                          userSelect: 'none',
                          '& .MuiChip-icon': {
                            marginLeft: '4px',
                            color: (plan.orderedQuantity || 0) > 0 ? 'white' : undefined
                          }
                        }}
                        title={(plan.orderedQuantity || 0) > 0 ? 
                          `${plan.orderedQuantity} items in transit - Click to manage` : 
                          'Click to create order'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assignedQuantity}
                        size="small"
                        sx={{
                          backgroundColor: assignedQuantity > 0 ? EQUIPMENT_STATUS.received.color : undefined,
                          color: assignedQuantity > 0 ? 'white' : undefined
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getCheckedOutQuantity(plan.id)}
                        size="small"
                        sx={{
                          backgroundColor: getCheckedOutQuantity(plan.id) > 0 ? EQUIPMENT_STATUS.checked_out.color : undefined,
                          color: getCheckedOutQuantity(plan.id) > 0 ? 'white' : undefined
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {plan.added_during_execution ? (
                        <Chip 
                          label="Added"
                          size="small"
                          color="info"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ) : stillNeeded > 0 ? (
                        <Chip 
                          label={stillNeeded}
                          size="small"
                          color="warning"
                          icon={<WarningIcon />}
                        />
                      ) : (
                        <Chip 
                          label="Complete"
                          size="small"
                          color="success"
                          icon={<CompleteIcon />}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.installed_quantity || 0}
                        size="small"
                        onClick={() => {
                          if (plan.id?.toString().startsWith('unplanned_')) {
                            enqueueSnackbar('Cannot update unplanned items - they are not assigned to this job yet', { variant: 'warning' });
                            return;
                          }
                          handleOpenInstalledDialog(plan);
                        }}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: (plan.installed_quantity || 0) > 0 ? EQUIPMENT_STATUS.installed.color : undefined,
                          color: (plan.installed_quantity || 0) > 0 ? 'white' : undefined
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={plan.notes ? "View notes" : "Add notes"}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPlan(plan);
                            setNotesDialog(true);
                          }}
                          color={plan.notes ? "primary" : "default"}
                        >
                          <NotesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {isEditing ? (
                        <Box display="flex" gap={0.5}>
                          <IconButton size="small" onClick={handleSaveEdit} disabled={saving}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={handleCancelEdit}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit quantity">
                            <IconButton size="small" onClick={() => handleStartEdit(plan)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!plan.id?.toString().startsWith('unplanned_') && (
                            <Tooltip title="Remove from plan">
                              <IconButton size="small" onClick={() => handleDeletePlan(plan)} color="error">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  
                  {/* Expandable Individual Items Row */}
                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={12}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom component="div" sx={{ mb: 2 }}>
                            Individual Equipment Items
                          </Typography>
                          {individualItems[plan.id] ? (
                            <Table size="small" sx={{ tableLayout: 'auto' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ minWidth: '140px' }}>Equipment ID</TableCell>
                                  <TableCell sx={{ minWidth: '120px' }}>Status</TableCell>
                                  <TableCell sx={{ minWidth: '100px' }}>Serial Number</TableCell>
                                  <TableCell sx={{ minWidth: '120px' }}>Vendor</TableCell>
                                  <TableCell sx={{ minWidth: '120px' }}>PO Number</TableCell>
                                  <TableCell sx={{ minWidth: '120px' }}>Warehouse</TableCell>
                                  <TableCell sx={{ minWidth: '100px' }}>Rack</TableCell>
                                  <TableCell sx={{ minWidth: '80px' }}>Bay</TableCell>
                                  <TableCell sx={{ minWidth: '100px' }}>Assigned By</TableCell>
                                  <TableCell sx={{ minWidth: '150px' }}>Notes</TableCell>
                                  <TableCell align="center" sx={{ minWidth: '120px' }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {individualItems[plan.id].map((item) => (
                                  <TableRow 
                                    key={item.id} 
                                    onClick={(e) => {
                                      // Don't trigger row click if clicking on buttons or interactive elements
                                      if (e.target.closest('button') || e.target.closest('[role="button"]')) return;
                                      handleViewHistory(item, plan);
                                    }}
                                    sx={{ 
                                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <TableCell>
                                      <Box>
                                        <Tooltip title={`${item.manufacturer || ''} ${item.model || ''} - ${item.description || ''}`}>
                                          <Typography variant="body2" fontWeight="500" sx={{ cursor: 'help' }}>
                                            {item.equipment_id || `EQ-${item.id}`}
                                          </Typography>
                                        </Tooltip>
                                        <Typography variant="caption" color="text.secondary">
                                          {(() => {
                                            const warehouse = item.warehouse_name || item.warehouse;
                                            const rack = item.rack_name || item.rack || item.zone;
                                            const bay = item.bay_name || item.bay;

                                            if (warehouse || rack || bay) {
                                              const parts = [warehouse, rack, bay].filter(Boolean);
                                              return parts.join(' / ');
                                            }
                                            return 'Job Level';
                                          })()}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title={
                                        item.status === 'assigned' ? 'Click to Check Out' :
                                        item.status === 'returned' ? 'Click to Check Out' :
                                        item.status === 'checked_out' ? 'Click to Mark Installed' :
                                        item.status === 'installed' ? 'Already Installed' :
                                        'Click to Update Status'
                                      }>
                                        <Chip
                                          label={getStatusDisplay(item.status || 'assigned').label}
                                          size="small"
                                          onClick={item.status !== 'installed' ? () => handleMarkInstalled(item, plan) : undefined}
                                          sx={{
                                            backgroundColor: getStatusDisplay(item.status || 'assigned').color,
                                            color: 'white',
                                            fontWeight: 500,
                                            minWidth: '80px',
                                            cursor: item.status !== 'installed' ? 'pointer' : 'default',
                                            '&:hover': item.status !== 'installed' ? {
                                              filter: 'brightness(0.9)',
                                              transform: 'scale(1.05)',
                                              transition: 'all 0.2s'
                                            } : {}
                                          }}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.serialNumber || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        value={item.vendor || ''}
                                        onChange={(e) => handleItemFieldChange(plan.id, item.id, 'vendor', e.target.value)}
                                        onBlur={() => handleSaveItemField(item.id, 'vendor', item.vendor)}
                                        placeholder="Vendor"
                                        variant="standard"
                                        sx={{ minWidth: '100px' }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        value={item.purchase_order || ''}
                                        onChange={(e) => handleItemFieldChange(plan.id, item.id, 'purchase_order', e.target.value)}
                                        onBlur={() => handleSaveItemField(item.id, 'purchase_order', item.purchase_order)}
                                        placeholder="PO #"
                                        variant="standard"
                                        sx={{ minWidth: '100px' }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.warehouse_name || item.warehouse || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.rack_name || item.rack || item.zone || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.bay_name || item.bay || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.assignedBy || 'System'}
                                      </Typography>
                                      {(item.assignedDate || item.receivedDate) && (
                                        <Typography variant="caption" color="text.secondary">
                                          {item.assignedDate || item.receivedDate}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        size="small"
                                        multiline
                                        maxRows={3}
                                        value={item.notes || ''}
                                        onChange={(e) => handleItemFieldChange(plan.id, item.id, 'notes', e.target.value)}
                                        onBlur={() => handleSaveItemField(item.id, 'notes', item.notes)}
                                        placeholder="Add notes..."
                                        variant="standard"
                                        sx={{ minWidth: '140px' }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Box display="flex" gap={0.5} justifyContent="center">
                                        <Tooltip title="Print QR Code">
                                          <IconButton
                                            size="small"
                                            onClick={() => handlePrintQRCode(item, plan)}
                                          >
                                            <QrCodeIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Change Status">
                                          <IconButton
                                            size="small"
                                            onClick={(event) => handleOpenStatusMenu(event, item)}
                                            color="primary"
                                          >
                                            <MoreVertIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        {item.status === 'installed' ? (
                                          <Tooltip title="Return Equipment">
                                            <IconButton
                                              size="small"
                                              onClick={() => openReturnDialog(item, plan)}
                                              color="warning"
                                            >
                                              <UndoIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : item.status === 'assigned' ? (
                                          <Tooltip title="Check Out to Technician">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMarkInstalled(item, plan)}
                                              color="primary"
                                            >
                                              <LocalShippingIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : item.status === 'checked_out' ? (
                                          <Tooltip title="Mark as Installed">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMarkInstalled(item, plan)}
                                              color="success"
                                            >
                                              <CompleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : item.status === 'returned' ? (
                                          <Tooltip title="Check Out to Technician">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMarkInstalled(item, plan)}
                                              color="primary"
                                            >
                                              <LocalShippingIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip title="Update Status">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMarkInstalled(item, plan)}
                                              color="primary"
                                            >
                                              <CompleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        <Tooltip title="More Actions">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleViewHistory(item, plan)}
                                            color="primary"
                                          >
                                            <HistoryIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              <Typography variant="body2" color="text.secondary">
                                Loading individual items...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Equipment Dialog */}
      <Dialog 
        open={addDialog} 
        onClose={(event, reason) => {
          // Only close on escape key, not backdrop click
          if (reason === 'escapeKeyDown') {
            handleCloseAddDialog();
          }
        }}
        disableEscapeKeyDown={false}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              Add Equipment to Job Plan
            </Typography>
            <Chip 
              label="Planning Mode"
              color="primary"
              size="small"
              variant="outlined"
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Search for equipment types and specify quantities needed for this job.
            </Typography>
            
            
            
            
            {/* Equipment Search - Hide when creating a template */}
            {!showTemplateForm && (
              <Paper variant="outlined" sx={{ p: 3, mb: 3, pointerEvents: 'all' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Equipment Selection
                </Typography>

                <Autocomplete
                options={equipmentLibrary}
                getOptionLabel={formatEquipmentOption}
                isOptionEqualToValue={(option, value) => option.id === value?.id || option.equipment_id === value?.equipment_id}
                value={selectedEquipment || null}
                onChange={(event, newValue) => setSelectedEquipment(newValue)}
                inputValue={searchText || ''}
                onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                loading={equipmentLibrary.length === 0 && searchText.length > 2}
                forcePopupIcon={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Equipment"
                    placeholder="Type equipment name, manufacturer, or model..."
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                      endAdornment: (
                        <>
                          {equipmentLibrary.length === 0 && searchText.length > 2 && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {option.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {option.barcode_pattern && (
                            <Chip 
                              label={`📊 ${option.barcode_pattern}`} 
                              size="small" 
                              variant="filled"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2'
                              }}
                            />
                          )}
                          {!option.barcode_pattern && option.isTemplate && (
                            <Chip 
                              label="No barcode" 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                borderColor: 'divider'
                              }}
                            />
                          )}
                          {option.isTemplate && (
                            <Chip 
                              label="Template" 
                              size="small" 
                              variant="outlined" 
                              color="secondary"
                              sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {option.manufacturer} {option.model}
                        {option.isTemplate 
                          ? ''
                          : ` • ${option.available_quantity} available in stock`
                        }
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  searchText.length < 3 
                    ? "Type at least 3 characters to search" 
                    : `No equipment found for "${searchText}"`
                }
                ListboxProps={{
                  style: { maxHeight: 200 }  // Limit dropdown height
                }}
                disablePortal  // Keep dropdown inside dialog
              />
              </Paper>
            )}

            {/* Template Creation Section - Shows when no templates match the search or when creating template */}
            {/* Only show if no equipment is selected AND either showing form or no matches found */}
            {!selectedEquipment && (showTemplateForm || (searchText.length >= 3 && equipmentLibrary.filter(item => item.isTemplate).length === 0)) && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 1, position: 'relative', zIndex: 10, pointerEvents: 'auto' }}>
                {!showTemplateForm ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      No equipment templates found for "{searchText}"
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onMouseDown={(e) => {
                        // Handle the template creation on mouse down instead
                        e.preventDefault();
                        e.stopPropagation();
                        
                        setShowTemplateForm(true);
                        
                        const searchParts = searchText.trim().split(' ');
                        setTemplateFormData({
                          manufacturer: searchParts[0] || '',
                          model: searchParts.slice(1).join(' ') || '',
                          description: searchText.trim(),
                          categoryId: '',
                          barcodePattern: '',
                          notes: ''
                        });
                      }}
                      sx={{
                        py: 1.5,
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    >
                      + Create Template for "{searchText}"
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      New Equipment Template Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            label="Barcode Pattern"
                            fullWidth
                            size="small"
                            value={templateFormData.barcodePattern}
                            onChange={(e) => setTemplateFormData(prev => ({ ...prev, barcodePattern: e.target.value }))}
                            placeholder="Enter a barcode to create pattern (e.g. SM58-2024-001)"
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ScanIcon />}
                            onClick={() => {
                              openBarcodeScanner((scannedBarcode) => {
                                setTemplateFormData(prev => ({ ...prev, barcodePattern: scannedBarcode }));
                              });
                            }}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Scan
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Manufacturer *"
                          fullWidth
                          size="small"
                          value={templateFormData.manufacturer}
                          onChange={(e) => setTemplateFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Model *"
                          fullWidth
                          size="small"
                          value={templateFormData.model}
                          onChange={(e) => setTemplateFormData(prev => ({ ...prev, model: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Description *"
                          fullWidth
                          size="small"
                          value={templateFormData.description}
                          onChange={(e) => setTemplateFormData(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Notes"
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={templateFormData.notes}
                          onChange={(e) => setTemplateFormData(prev => ({ ...prev, notes: e.target.value }))}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
            )}

            {/* Update Template Section - Only shows for templates created in this session */}
            {selectedEquipment?.isTemplate && newlyCreatedTemplateIds.has(selectedEquipment.templateId) && (
              <Paper variant="outlined" sx={{ p: 3, mb: 2, bgcolor: 'info.50', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'info.main' }}>
                  Template Selected: {selectedEquipment.templateName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You can add a barcode to this template or update its information.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ScanIcon />}
                    onClick={() => handleScanBarcodeForTemplate()}
                    size="small"
                  >
                    Scan Barcode
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditTemplate()}
                    size="small"
                  >
                    Edit Template
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Quantity and Site Assignment - Show for both template creation and equipment selection */}
            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Quantity & Assignment
              </Typography>
              <Box display="flex" gap={3} sx={{ alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 200 }}>
                  <TextField
                    label="Planned Quantity"
                    type="number"
                    value={plannedQuantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string for better UX, convert to number on blur
                      if (value === '') {
                        setPlannedQuantity('');
                      } else {
                        const numValue = parseInt(value, 10);
                        if (!isNaN(numValue) && numValue >= 0) {
                          setPlannedQuantity(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure minimum of 1 when field loses focus
                      if (plannedQuantity === '' || plannedQuantity < 1) {
                        setPlannedQuantity(1);
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    inputProps={{ min: 1 }}
                    helperText="How many units needed"
                  />
                </Box>
              
                
                <Box sx={{ flex: 1 }}>
                <Autocomplete
                  options={sites}
                  getOptionLabel={(option) => option.name || 'Unknown Site'}
                  value={selectedSite}
                  onChange={(event, newValue) => setSelectedSite(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Site"
                      placeholder="Select site for this equipment..."
                      fullWidth
                      variant="outlined"
                      helperText="Where will this equipment be used?"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {option.name}
                        </Typography>
                        {option.location && (
                          <Typography variant="caption" color="text.secondary">
                            {option.location}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                )}
                noOptionsText="No sites available for this job"
              />
                </Box>
              </Box>
            </Paper>

            {selectedEquipment && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Selected:</strong> {selectedEquipment.description}<br/>
                  <strong>Available:</strong> {selectedEquipment.available_quantity} units
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (showTemplateForm) {
              setShowTemplateForm(false);
              setTemplateFormData({
                manufacturer: '',
                model: '',
                description: '',
                categoryId: '',
                barcodePattern: '',
                notes: ''
              });
            } else {
              handleCloseAddDialog();
            }
          }}>Cancel</Button>
          <Button
            onClick={showTemplateForm ? handleSaveTemplateInline : handleAddEquipment}
            variant="contained"
            disabled={
              showTemplateForm
                ? (!templateFormData.manufacturer || !templateFormData.model || !templateFormData.description || saving)
                : (!selectedEquipment || !plannedQuantity || plannedQuantity < 1 || saving)
            }
            startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {saving ? 'Creating...' : (showTemplateForm ? 'Create Template & Add to Plan' : 'Add to Plan')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Activity Timeline */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Planning Activity
        </Typography>
        <PlanningActivityTimeline jobId={jobId} siteId={selectedSite?.id} refreshTrigger={activityRefreshTrigger} />
      </Box>

      {/* Order Equipment Dialog */}
      <Dialog 
        open={orderDialogOpen} 
        onClose={handleCloseOrderDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Order Equipment
        </DialogTitle>
        <DialogContent>
          {selectedPlanForOrder && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Equipment:</strong> {selectedPlanForOrder.description}<br/>
                  <strong>Planned:</strong> {selectedPlanForOrder.needed_quantity || 0} units<br/>
                  <strong>Available:</strong> {selectedPlanForOrder.availableQuantity || 0} units<br/>
                  <strong>Already Ordered:</strong> {selectedPlanForOrder.orderedQuantity || 0} units
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Quantity to Order"
                    type="number"
                    fullWidth
                    value={orderFormData.orderedQuantity}
                    onChange={(e) => handleOrderFormChange('orderedQuantity', parseInt(e.target.value, 10) || 0)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Order Date"
                    type="date"
                    fullWidth
                    value={orderFormData.orderDate}
                    onChange={(e) => handleOrderFormChange('orderDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Vendor"
                    fullWidth
                    value={orderFormData.vendor}
                    onChange={(e) => handleOrderFormChange('vendor', e.target.value)}
                    placeholder="e.g., Home Depot, Amazon"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Order Reference"
                    fullWidth
                    value={orderFormData.orderReference}
                    onChange={(e) => handleOrderFormChange('orderReference', e.target.value)}
                    placeholder="PO #, Order #"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={2}
                    value={orderFormData.notes}
                    onChange={(e) => handleOrderFormChange('notes', e.target.value)}
                    placeholder="Special instructions, delivery notes..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitOrder}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Updating...' : 'Update Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Installed Equipment Dialog */}
      <Dialog 
        open={installedDialogOpen} 
        onClose={handleCloseInstalledDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Update Installed Quantity
        </DialogTitle>
        <DialogContent>
          {selectedPlanForInstalled && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Equipment:</strong> {selectedPlanForInstalled.description}<br/>
                  <strong>Planned:</strong> {selectedPlanForInstalled.needed_quantity || 0} units<br/>
                  <strong>Received:</strong> {selectedPlanForInstalled.assignedQuantity || getAssignedQuantity(selectedPlanForInstalled.id)} units<br/>
                  <strong>Currently Installed:</strong> {selectedPlanForInstalled.installed_quantity || 0} units
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Installed Quantity"
                    type="number"
                    fullWidth
                    value={installedFormData.installedQuantity}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 0;
                      const receivedQty = selectedPlanForInstalled.assignedQuantity || getAssignedQuantity(selectedPlanForInstalled.id);
                      const maxValue = Math.min(value, receivedQty);
                      handleInstalledFormChange('installedQuantity', maxValue);
                    }}
                    inputProps={{ 
                      min: 0,
                      max: selectedPlanForInstalled.assignedQuantity || getAssignedQuantity(selectedPlanForInstalled.id)
                    }}
                    error={installedFormData.installedQuantity > (selectedPlanForInstalled.assignedQuantity || getAssignedQuantity(selectedPlanForInstalled.id))}
                    helperText={
                      installedFormData.installedQuantity > (selectedPlanForInstalled.assignedQuantity || getAssignedQuantity(selectedPlanForInstalled.id))
                        ? `Cannot exceed received quantity (${selectedPlanForInstalled.assignedQuantity || getAssignedQuantity(selectedPlanForInstalled.id)} units)`
                        : "Number of items delivered to technicians and taken to the job site"
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes (Optional)"
                    fullWidth
                    multiline
                    rows={2}
                    value={installedFormData.notes}
                    onChange={(e) => handleInstalledFormChange('notes', e.target.value)}
                    placeholder="Installation notes, technician names..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInstalledDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitInstalled}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Updating...' : 'Update Installed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status History Dialog */}
      <StatusHistoryDialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setSelectedEquipmentForHistory(null);
        }}
        equipment={selectedEquipmentForHistory}
      />
      
      {/* Individual Item Notes Dialog */}
      <Dialog
        open={itemNotesDialog}
        onClose={() => setItemNotesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Equipment Notes
          {selectedItemForNotes && (
            <Typography variant="body2" color="text.secondary">
              {selectedItemForNotes.equipment_id || `EQ-${selectedItemForNotes.id}`} - {selectedItemForNotes.manufacturer} {selectedItemForNotes.model}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedItemForNotes && (
            <Box>
              {/* Existing notes */}
              {selectedItemForNotes.notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Notes:
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    maxHeight: 200,
                    overflowY: 'auto'
                  }}>
                    {selectedItemForNotes.notes.split('\n').map((note, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        {note}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Add new note */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Add New Note:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={newItemNote}
                  onChange={(e) => setNewItemNote(e.target.value)}
                  placeholder="Enter your note here..."
                  variant="outlined"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemNotesDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddItemNote}
            variant="contained"
            disabled={!newItemNote.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Adding...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog 
        open={notesDialog} 
        onClose={() => {
          setNotesDialog(false);
          setSelectedPlan(null);
          setNewNote('');
        }}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Equipment Notes - {selectedPlan?.description || 'Equipment'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPlan?.notes ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Previous Notes:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 200, overflowY: 'auto' }}>
                <LinkifiedText 
                  text={selectedPlan.notes}
                  typographyProps={{ 
                    variant: "body2", 
                    component: "pre", 
                    sx: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                  }}
                />
              </Paper>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              No previous notes for this equipment.
            </Typography>
          )}
          
          <Typography variant="subtitle2" gutterBottom>
            Add New Note:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Enter note here... (timestamp and user will be added automatically)"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNotesDialog(false);
            setSelectedPlan(null);
            setNewNote('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddNote}
            variant="contained"
            disabled={!newNote.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Add Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Create/Edit Dialog */}
      <Dialog 
        open={templateDialogOpen} 
        onClose={handleCloseTemplateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTemplateId ? 'Edit Template' : 'Create New Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Manufacturer *"
                  fullWidth
                  required
                  value={templateFormData.manufacturer}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                  placeholder="e.g., Bosch"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Model *"
                  fullWidth
                  required
                  value={templateFormData.model}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="e.g., GLI12V-300"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description *"
                  fullWidth
                  required
                  value={templateFormData.description}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed description of the equipment"
                />
              </Grid>
              {categories.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Category (Optional)</InputLabel>
                    <Select
                      value={templateFormData.categoryId}
                      onChange={(e) => setTemplateFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                      label="Category (Optional)"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {categories.map(cat => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Barcode Pattern (Optional)"
                    fullWidth
                    value={templateFormData.barcodePattern}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, barcodePattern: e.target.value }))}
                    placeholder="Leave empty if no barcode pattern"
                    helperText="Example: BOX-#### for sequential numbering"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ScanIcon />}
                    onClick={() => {
                      openBarcodeScanner((scannedBarcode) => {
                        setTemplateFormData(prev => ({ ...prev, barcodePattern: scannedBarcode }));
                      });
                    }}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    Scan
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes (Optional)"
                  fullWidth
                  multiline
                  rows={3}
                  value={templateFormData.notes}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes about this template"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={!templateFormData.name || !templateFormData.manufacturer || !templateFormData.model || !templateFormData.description}
          >
            {editingTemplateId ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Barcode Scanner Dialog */}
      <Dialog
        open={scannerDialogOpen}
        onClose={() => setScannerDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Scan Barcode</DialogTitle>
        <DialogContent>
          <BarcodeScanner 
            onScanSuccess={handleBarcodeScanResult}
            onScanError={(err) => {
              console.error('Scan error:', err);
              enqueueSnackbar('Failed to scan barcode', { variant: 'error' });
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScannerDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status change menu for individual items */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {Object.entries(EQUIPMENT_STATUS).map(([statusKey, statusConfig]) => (
          <MenuItem 
            key={statusKey}
            onClick={() => handleStatusChange(statusKey)}
            disabled={statusMenuSelectedItem?.status === statusKey}
          >
            <Chip
              label={statusConfig.label}
              size="small"
              sx={{ 
                backgroundColor: statusConfig.color, 
                color: 'white', 
                width: '100px',
                opacity: statusMenuSelectedItem?.status === statusKey ? 0.5 : 1
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Checkout Equipment Dialog */}
      <Dialog
        open={checkoutDialogOpen}
        onClose={() => {
          setCheckoutDialogOpen(false);
          setSelectedItemForCheckout(null);
          setSelectedPlanForCheckout(null);
          setSelectedTechnician('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Checkout Equipment to Technician</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedItemForCheckout && selectedPlanForCheckout && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Checking out: {selectedItemForCheckout.equipment_id} - {selectedPlanForCheckout.description}
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select technician:
            </Typography>

            <FormControl fullWidth>
              <Autocomplete
                options={technicians}
                getOptionLabel={(option) => {
                  // Handle both string (typed) and object (selected from list)
                  if (typeof option === 'string') return option;
                  return option?.name || option?.email || '';
                }}
                value={selectedTechnician}
                onChange={(event, newValue) => {
                  // Extract name from object or use string directly
                  if (typeof newValue === 'string') {
                    setSelectedTechnician(newValue);
                  } else if (newValue?.name) {
                    setSelectedTechnician(newValue.name);
                  } else {
                    setSelectedTechnician('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Technician"
                    placeholder="Select or type technician name"
                    required
                  />
                )}
                freeSolo
                sx={{ mb: 2 }}
              />
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              Equipment will be marked as checked out to the selected technician. Click the checkout icon again to mark as installed.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCheckoutDialogOpen(false);
            setSelectedItemForCheckout(null);
            setSelectedPlanForCheckout(null);
            setSelectedTechnician('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleCheckoutSubmit}
            variant="contained"
            disabled={saving || !selectedTechnician}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            Checkout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Equipment Dialog */}
      <Dialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Return Equipment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {returnItem && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Returning: {returnItem.equipment_id} - {returnItem.description}
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select return option:
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={returnType}
                onChange={(e) => setReturnType(e.target.value)}
              >
                <MenuItem value="job">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon fontSize="small" />
                    Return to Job (Keep assigned to this job)
                  </Box>
                </MenuItem>
                <MenuItem value="warehouse">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeIcon fontSize="small" />
                    Return to Warehouse (Available for any job)
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {returnType === 'warehouse' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Select warehouse location (optional):
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Warehouse</InputLabel>
                    <Select
                      value={returnLocation.warehouse_id || ''}
                      onChange={(e) => {
                        setReturnLocation({
                          ...returnLocation,
                          warehouse_id: e.target.value
                        });
                      }}
                      label="Warehouse"
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="">No specific location</MenuItem>
                      {warehouses.map(w => (
                        <MenuItem key={w.id} value={w.id}>
                          {w.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Rack</InputLabel>
                    <Select
                      value={returnLocation.rack_id || ''}
                      onChange={(e) => {
                        setReturnLocation({
                          ...returnLocation,
                          rack_id: e.target.value
                        });
                      }}
                      label="Rack"
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="">No specific rack</MenuItem>
                      {racks.map(r => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Bay</InputLabel>
                    <Select
                      value={returnLocation.bay_id || ''}
                      onChange={(e) => setReturnLocation({
                        ...returnLocation,
                        bay_id: e.target.value
                      })}
                      label="Bay"
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="">No specific bay</MenuItem>
                      {bays.map(b => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReturnEquipment}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <ReturnIcon />}
          >
            {returnType === 'warehouse' ? 'Return to Warehouse' : 'Return to Job'}
          </Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
}